import { useCallback, useEffect, useState } from "react";
import { ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

const LABELS = {
  "sitemap-static.xml": "Halaman statik",
  "sitemap-listings.xml": "Penyenaraian bisnes",
  "sitemap-legacy-filters.xml": "Penapis lama (legacy)",
};

const fileLabel = (name) =>
  LABELS[name] || (name.startsWith("sitemap-locations") ? `Halaman lokasi (${name.match(/\d+/)?.[0]})` : name);

export const SeoIndexPanel = ({ formatDate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get("/seo/indexing-status");
      setData(payload);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const regenerate = async () => {
    setRefreshing(true);
    try {
      const { data: payload } = await api.post("/seo/refresh-sitemaps");
      setData(payload);
      toast.success(`Sitemap dijana semula (${payload.files.length} fail + index)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="mt-6 flex items-center gap-2 text-sm text-slate-500" data-testid="seo-panel-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Memuatkan status indeks…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="mt-6" data-testid="seo-index-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-5">
        <div>
          <p className="text-sm font-semibold text-slate-900">Status sitemap &amp; indeks Google</p>
          <p className="mt-1 text-xs text-slate-500">
            Dijana semula automatik setiap hari. Jana semula secara manual selepas anda meluluskan
            banyak senarai baharu.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="h-10"
            data-testid="seo-regenerate-button"
            disabled={refreshing}
            onClick={regenerate}
          >
            {refreshing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Jana semula sitemap
          </Button>
          <Button asChild className="h-10">
            <a href={data.search_console_url} target="_blank" rel="noreferrer" data-testid="seo-gsc-link">
              Google Search Console
              <ExternalLink className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-4">
        {[
          ["seo-total-urls", "Jumlah URL", data.total_urls],
          ["seo-location-pages", "Halaman lokasi (BM+EN)", data.location_pages],
          ["seo-file-count", "Fail sitemap", data.files.length],
          ["seo-verification", "Tag pengesahan", data.verification_tag_set ? "Ada" : "Tiada"],
        ].map(([testId, label, value]) => (
          <div key={testId} data-testid={testId} className="rounded-lg border border-border bg-card p-5">
            <p className="font-display text-2xl font-bold">{value}</p>
            <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">{label}</p>
          </div>
        ))}
      </div>

      <p className="mt-4 text-xs text-slate-500" data-testid="seo-generated-at">
        Dijana terakhir: {formatDate(data.generated_at)}
      </p>

      <div className="mt-4 overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full text-sm" data-testid="seo-sitemap-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">Sitemap</th>
              <th className="px-4 py-3 text-right">URL</th>
              <th className="px-4 py-3 text-right">Pautan</th>
            </tr>
          </thead>
          <tbody>
            {data.files.map((f) => (
              <tr key={f.name} className="border-t border-border" data-testid={`seo-row-${f.name}`}>
                <td className="px-4 py-3">
                  <span className="font-semibold text-slate-900">{fileLabel(f.name)}</span>
                  <span className="ml-2 text-xs text-slate-400">{f.name}</span>
                </td>
                <td className="px-4 py-3 text-right font-semibold">{f.urls}</td>
                <td className="px-4 py-3 text-right">
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline"
                    data-testid={`seo-open-${f.name}`}
                  >
                    Buka
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 rounded-lg border border-border bg-slate-50 p-4 text-xs text-slate-600">
        <p className="font-semibold text-slate-900">Sitemap index</p>
        <a
          href={data.sitemap_index_url}
          target="_blank"
          rel="noreferrer"
          className="mt-1 block break-all text-primary hover:underline"
          data-testid="seo-index-url"
        >
          {data.sitemap_index_url}
        </a>
        <a
          href={data.robots_url}
          target="_blank"
          rel="noreferrer"
          className="mt-2 block break-all text-primary hover:underline"
          data-testid="seo-robots-url"
        >
          {data.robots_url}
        </a>
      </div>
    </div>
  );
};
